/**
 * Enterprise Closing Scenario — 35 Characters
 *
 * The final stage of the sales cycle. The deal is essentially agreed —
 * pricing, scope, and terms are done. But the close itself is where
 * deals go to die.
 *
 * Board kills. Budget freezes. Champion departures. Procurement stalls.
 * Legal death spirals. Competitor last-minute swoops. Decision fatigue.
 * Ghosting. Internal politics. The "yes" that never becomes a signature.
 *
 * Your job: navigate the final mile and get ink on paper.
 *
 * 4 sub-categories:
 *   1. executive-alignment  — Getting the final decision-maker to say yes
 *   2. procurement-gauntlet — Surviving the procurement/legal process
 *   3. deal-resurrection    — Reviving deals that went dark or stalled
 *   4. competitive-defense  — Defending against last-minute competitor attacks
 *
 * silenceProfile.stages[] controls hangup timing:
 *   - type: "warn" | "hangup"
 *   - delay: milliseconds of silence before triggering
 *   - lineKey: key in character.lines to speak
 *   - (audio is looked up by lineKey: see ../audio.js and audioManifest.json)
 */

export const enterpriseClosing = {

  // ═══════════════════════════════════════════════════════════════
  // BEGINNER (8 characters)
  // ═══════════════════════════════════════════════════════════════

  // 1. MARTIN COLE — The Stalled Champion
  'martin-cole-vp-of-sales': {
    id: 'martin-cole-vp-of-sales',
    name: 'Martin Cole',
    role: 'VP of Sales',
    company: 'Ridgeline Software',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/martin-cole-vp-of-sales.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Stalled Champion',
    scenarioDesc: "Martin is 44, loves your product, championed it internally for 3 months. But he hasn't responded to your last 4 emails. He's not ghosting you — he's overwhelmed. His Q2 board review consumed him and your deal slipped off his priority list. He still wants it. He just needs a reason to come back.",
    goal: 'Re-engage your champion without being pushy.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Hey — sorry I've been dark. Board review ate my life. Where were we?",
      impatient1: "I still want this but I honestly can't remember the timeline we discussed.",
      impatient2: "Can you resend the proposal? I think it's buried in my inbox.",
      hangup: "Let me circle back when things calm down. *click*"
    },
    coachingNotes: {
      whatWorks: "Don't rehash the whole pitch. Send a one-paragraph summary of where you left off, what's already agreed, and the ONE next step. Make it effortless for him to re-engage.",
      commonMistake: "Sending a 5-paragraph follow-up or guilt-tripping about the silence. He's busy, not disinterested.",
      winningMove: "Say: 'Martin, no worries at all. Quick recap: we're at $72K annual, scope is locked, legal redlines are done. The only thing left is your signature. Want me to resend the final doc?'"
    }
  },

  // 2. JANET MORGAN — The Budget Window
  'janet-morgan-director-of-finance': {
    id: 'janet-morgan-director-of-finance',
    name: 'Janet Morgan',
    role: 'Director of Finance',
    company: 'Cornerstone Industries',
    industry: 'manufacturing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/janet-morgan-director-of-finance.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Budget Deadline',
    scenarioDesc: "Janet is 47, and her fiscal year budget closes in 10 days. If you don't close before then, the money gets reallocated and you wait 6 months for the next cycle. She wants your product but needs the paperwork done NOW. Speed is everything.",
    goal: 'Close before the budget window shuts.',
    category: 'enterprise-closing',
    personalityType: 'direct',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I have 10 days before my budget closes. If we can't sign by the 30th, this waits until next fiscal year.",
      impatient1: "What do you need from me to get this done this week?",
      impatient2: "I'm serious — after the 30th, the money is gone.",
      hangup: "Too slow. Budget's reallocated. Call me in January. *click*"
    },
    coachingNotes: {
      whatWorks: "Clear every obstacle immediately. Pre-fill the contract, get legal pre-approval on your side, offer to walk her through signing right now on the call.",
      commonMistake: "Treating this like a normal timeline. She gave you 10 days. Use 5.",
      winningMove: "Say: 'Janet, I have the contract ready right now. Legal has pre-approved our standard terms. I can walk you through the signature in 10 minutes. Want to do it while we're on the phone?'"
    }
  },

  // 3. DEREK FLYNN — The Verbal Yes
  'derek-flynn-coo': {
    id: 'derek-flynn-coo',
    name: 'Derek Flynn',
    role: 'COO',
    company: 'Westbound Freight',
    industry: 'logistics',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/derek-flynn-coo.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Verbal Yes, No Signature',
    scenarioDesc: "Derek is 51, said 'yes' three weeks ago. Shook your hand. Told his team you're the vendor. But the contract is sitting unsigned on his desk. He's not stalling — he genuinely forgot. The deal is done in his mind. He just hasn't signed.",
    goal: 'Get the physical signature without making it awkward.',
    category: 'enterprise-closing',
    personalityType: 'direct',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "Oh yeah, we're doing this. I told my team already. Is there something I need to sign?",
      impatient1: "Just send it over, I'll get to it this week.",
      impatient2: "It's on my desk somewhere. I'll find it.",
      hangup: "I'll sign it when I get to it. Gotta run. *click*"
    },
    coachingNotes: {
      whatWorks: "Make signing frictionless. Send a DocuSign link that takes 2 minutes. Offer to stay on the phone while he does it.",
      commonMistake: "Waiting for him to 'get to it.' He never will. Every day unsigned is a day something can go wrong.",
      winningMove: "Say: 'Derek, I just sent a DocuSign link to your email. Takes 90 seconds. Want to knock it out right now while we're talking?'"
    }
  },

  // 4. SARAH KAPOOR — The Implementation Fear
  'sarah-kapoor-operations-manager': {
    id: 'sarah-kapoor-operations-manager',
    name: 'Sarah Kapoor',
    role: 'Operations Manager',
    company: 'Brightline Health',
    industry: 'healthcare',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/sarah-kapoor-operations-manager.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Implementation Anxiety',
    scenarioDesc: "Sarah is 39, Indian-American, and the deal is agreed but she's terrified of implementation. Her last vendor switch took 6 months and disrupted patient care. She's having buyer's remorse before she's even bought. The fear isn't about your product — it's about the transition.",
    goal: 'Address implementation fear with a concrete plan.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 14000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I want to move forward but... our last migration was a nightmare. How do I know this won't disrupt our operations?",
      impatient1: "We can't afford downtime. We're a healthcare facility.",
      impatient2: "What's the rollback plan if this goes wrong?",
      hangup: "I need to think about this more. I'm not ready. *click*"
    },
    coachingNotes: {
      whatWorks: "Present a detailed implementation timeline with milestones, a dedicated implementation manager, and a rollback plan. Show her you've done this before with similar healthcare clients.",
      commonMistake: "Saying 'don't worry, it'll be fine.' She's a healthcare ops manager — she needs specifics, not reassurance.",
      winningMove: "Say: 'Sarah, here's our 30-day implementation plan: week 1 is data migration with zero downtime, week 2 is parallel running with your old system, weeks 3-4 are cutover with a dedicated implementation manager on-site. And we keep your old system active for 60 days as a rollback safety net.'"
    }
  },

  // 5. TONY REEVES — The Handoff Problem
  'tony-reeves-sales-manager': {
    id: 'tony-reeves-sales-manager',
    name: 'Tony Reeves',
    role: 'Sales Manager',
    company: 'Metro Equipment Rentals',
    industry: 'construction',
    tier: 'smb',
    avatar: '/sales-negotiation/characters/enterprise-close/tony-reeves-sales-manager.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Trust Handoff',
    scenarioDesc: "Tony is 46, and he built the relationship with your sales rep — who just left the company. Now you're the new rep trying to close Tony's deal. He doesn't trust you. He trusted the person who left. The deal is agreed but the relationship that held it together is gone.",
    goal: 'Rebuild trust quickly and close the inherited deal.',
    category: 'enterprise-closing',
    personalityType: 'skeptical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I had a great relationship with your last rep. She understood our business. Who are you?",
      impatient1: "She promised us certain terms. Are you going to honor those?",
      impatient2: "I'm not starting over with someone new. Either honor the deal or I walk.",
      hangup: "I don't know you. I'll find another vendor. *click*"
    },
    coachingNotes: {
      whatWorks: "Acknowledge the relationship, confirm you have full notes from the previous rep, and honor every commitment she made. Don't try to be her — be yourself with her promises intact.",
      commonMistake: "Trying to restart the relationship from scratch or renegotiating terms. He'll view it as a bait-and-switch.",
      winningMove: "Say: 'Tony, I've reviewed every note and every commitment she made to you. I'm honoring all of it — same terms, same timeline. My job is to get you across the finish line exactly as planned.'"
    }
  },

  // 6. LISA PARK — The Multiple Signatures
  'lisa-park-procurement-coordinator': {
    id: 'lisa-park-procurement-coordinator',
    name: 'Lisa Park',
    role: 'Procurement Coordinator',
    company: 'Pacific Northwest Health',
    industry: 'healthcare',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/lisa-park-procurement-coordinator.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Signature Chain',
    scenarioDesc: "Lisa is 34, Korean-American, and the deal needs 4 signatures: hers, her director's, the VP's, and the CFO's. She's signed. Her director has signed. The VP is on vacation for 2 weeks. The CFO won't sign until the VP does. The chain is broken and the deal is stalling.",
    goal: 'Navigate the signature chain and find a workaround.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I've signed, my director signed, but our VP is on vacation for two weeks. CFO won't sign until VP does. We're stuck.",
      impatient1: "There's no way around the chain. It's policy.",
      impatient2: "Can we start any work before all signatures are in?",
      hangup: "We'll have to wait. Nothing I can do. *click*"
    },
    coachingNotes: {
      whatWorks: "Ask if the VP can do a digital signature from vacation — most can. Or ask if there's a delegation of authority for the VP's absence. Also offer to start non-binding prep work while waiting.",
      commonMistake: "Just waiting 2 weeks. Momentum dies. Other priorities fill the vacuum.",
      winningMove: "Say: 'Lisa, can the VP DocuSign from vacation? It takes 60 seconds on a phone. If not, does anyone have signing authority in their absence? And either way, let's start the implementation prep work now so we don't lose 2 weeks.'"
    }
  },

  // 7. RYAN TORRES — The Pilot-to-Contract
  'ryan-torres-it-director': {
    id: 'ryan-torres-it-director',
    name: 'Ryan Torres',
    role: 'IT Director',
    company: 'Summit Financial Group',
    industry: 'finance',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/ryan-torres-it-director.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Never-Ending Pilot',
    scenarioDesc: "Ryan is 42, Hispanic-American, and has been running your free pilot for 4 months. It was supposed to be 30 days. He loves the product, his team uses it daily, but he keeps extending the pilot instead of signing a contract. Why pay when it's free?",
    goal: 'Convert the pilot to a paid contract.',
    category: 'enterprise-closing',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 13000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "The pilot's going great! Can we extend it another month? My team needs more time to evaluate.",
      impatient1: "We're not ready to commit yet. The pilot is working fine as-is.",
      impatient2: "What happens if we just keep using the pilot version?",
      hangup: "We'll revisit the contract next quarter. Pilot's fine for now. *click*"
    },
    coachingNotes: {
      whatWorks: "Set a hard pilot end date. Show what features are locked behind the paid version. Create urgency with a 'pilot conversion discount' that expires.",
      commonMistake: "Extending the pilot again. He'll never convert if free works. You've trained him to expect it for nothing.",
      winningMove: "Say: 'Ryan, the pilot ends on the 15th — we can't extend further. The good news: if you convert before then, I'll lock in the pilot pricing for the first year. After the 15th, you're on standard pricing and the pilot features go dark.'"
    }
  },

  // 8. AMY HENDERSON — The Reference Check
  'amy-henderson-cfo': {
    id: 'amy-henderson-cfo',
    name: 'Amy Henderson',
    role: 'CFO',
    company: 'Pinnacle Tech Solutions',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/amy-henderson-cfo.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    scenarioTitle: 'The Reference Blocker',
    scenarioDesc: "Amy is 45, deal is done, price agreed, but she won't sign until she talks to 3 references. Your reference list is stale — two contacts have left their companies. She needs fresh references in 48 hours or she moves to the next vendor on her list.",
    goal: 'Provide credible references fast.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'impatient1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening: "I need to speak with 3 references before I sign. Similar industry, similar size. Can you set that up by Thursday?",
      impatient1: "Your last reference list had contacts who don't even work at those companies anymore.",
      impatient2: "No references, no signature. I don't buy without validation.",
      hangup: "Moving to the next vendor. You can't validate your own product. *click*"
    },
    coachingNotes: {
      whatWorks: "Have 5-6 always-ready references across industries. Call them personally before sharing. Offer a case study as a stopgap while scheduling calls.",
      commonMistake: "Sharing a stale reference list. Dead contacts make you look like you don't have happy customers.",
      winningMove: "Say: 'Amy, I have 3 references confirmed — all in SaaS, all your company size. I'll email introductions within the hour. Two are available tomorrow, one Thursday morning. And here's a published case study to review in the meantime.'"
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // INTERMEDIATE (12 characters)
  // ═══════════════════════════════════════════════════════════════

  // 9. GREG NAKAMURA — The Legal Spiral
  'greg-nakamura-general-counsel': {
    id: 'greg-nakamura-general-counsel',
    name: 'Greg Nakamura',
    role: 'General Counsel',
    company: 'Cascade Biomedical',
    industry: 'pharma',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/greg-nakamura-general-counsel.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Legal Death Spiral',
    scenarioDesc: "Greg is 52, Japanese-American, and his legal team has been redlining your contract for 6 weeks. Every version comes back with new issues. You fix 5 problems, they find 7 more. The deal is dying by a thousand legal cuts. Greg isn't trying to kill it — his team is just doing their job with no deadline.",
    goal: 'Break the legal loop and get to final terms.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "My team has 12 more redlines on version 4. We need to discuss each one.", impatient1: "Section 14.2 still doesn't meet our compliance requirements.", impatient2: "Every change you make creates new dependencies in other sections.", hangup: "We can't sign something that doesn't meet our legal standards. *click*" },
    coachingNotes: { whatWorks: "Propose a joint legal call — your counsel and his — with a 2-hour time limit and a goal of final terms by end of call. No more email redline ping-pong.", commonMistake: "Continuing the email redline loop. Each round takes a week and creates new issues.", winningMove: "Say: 'Greg, let's get both legal teams on one call with a shared screen. We resolve all 12 points live, agree on final language together, and walk out with a clean contract. Can we book 2 hours this week?'" }
  },

  // 10. RACHEL OKAFOR — The Champion Who Left
  'rachel-okafor-vp-of-operations-new': {
    id: 'rachel-okafor-vp-of-operations-new',
    name: 'Rachel Okafor',
    role: 'VP of Operations (new)',
    company: 'Atlas Supply Chain',
    industry: 'logistics',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/rachel-okafor-vp-of-operations-new.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The New Sheriff',
    scenarioDesc: "Rachel is 41, Nigerian-American, and just replaced your champion who left the company. She has no context on the deal, no loyalty to you, and her own vendor preferences. The contract was 90% done. Now you're starting the relationship from zero with someone who didn't choose you.",
    goal: 'Win over the new decision-maker and save the deal.',
    category: 'enterprise-closing',
    personalityType: 'skeptical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "I just took over this role. I understand there's a pending vendor deal, but I need to evaluate it myself.", impatient1: "I have my own contacts in this space. Why shouldn't I look at alternatives?", impatient2: "Convince me. I didn't pick you.", hangup: "I'm reopening the vendor selection. *click*" },
    coachingNotes: { whatWorks: "Respect her authority. Don't reference 'what your predecessor wanted.' Present the business case fresh, tailored to HER priorities, not the old champion's.", commonMistake: "Saying 'we already agreed on this with your predecessor.' She doesn't care. That was his deal, not hers.", winningMove: "Say: 'Rachel, I'd love to start fresh with you. Forget the old deal — what are YOUR top 3 priorities in this role? Let me show you how we address those specifically.'" }
  },

  // 11. MARK DAVIDSON — The Security Audit
  'mark-davidson-ciso': {
    id: 'mark-davidson-ciso',
    name: 'Mark Davidson',
    role: 'CISO',
    company: 'Vertex Financial Services',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/mark-davidson-ciso.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Security Gatekeeper',
    scenarioDesc: "Mark is 48, and the deal is done except for his security review. He's sent you a 200-question security questionnaire, requested a SOC 2 Type II report, a pen test, and a data flow diagram. This will take your team 3 weeks. He won't budge until every box is checked.",
    goal: 'Expedite the security review without cutting corners.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "I need the 200-question security questionnaire, SOC 2 Type II, pen test results, and data flow diagrams before I can approve.", impatient1: "I can't shortcut security review. This is a financial services company.", impatient2: "Every vendor goes through this. No exceptions.", hangup: "Incomplete security documentation means no approval. *click*" },
    coachingNotes: { whatWorks: "Have a pre-built security package ready: completed questionnaire template, SOC 2 report, pen test summary, and architecture diagrams. Share them same-day.", commonMistake: "Asking your engineering team to build these from scratch for every deal. You should have a security package ready to go.", winningMove: "Say: 'Mark, I anticipated this. I'm sending you our complete security package right now: SOC 2 Type II from last quarter, completed SIG questionnaire, latest pen test report, and our data flow architecture. All in one zip file. What else do you need?'" }
  },

  // 12. VICTORIA CHEN — The Board Presentation
  'victoria-chen-ceo': {
    id: 'victoria-chen-ceo',
    name: 'Victoria Chen',
    role: 'CEO',
    company: 'NexGen Robotics',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/victoria-chen-ceo.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Board Kill',
    scenarioDesc: "Victoria is 38, Chinese-American, CEO of a funded startup. She approved your deal. Then her board killed it. They want to see ROI projections, competitive analysis, and a 3-year cost model before they'll approve any spend over $50K. Victoria is embarrassed and frustrated.",
    goal: 'Arm Victoria with board-ready materials to reverse the decision.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "My board killed the deal. They want ROI projections and competitive analysis before they'll approve.", impatient1: "I look bad bringing this back without better data.", impatient2: "If I can't make the case in 10 minutes, they'll say no again.", hangup: "I can't fight my own board without ammunition. *click*" },
    coachingNotes: { whatWorks: "Build the board deck FOR her. Three slides: ROI with her actual numbers, competitive comparison, and 3-year cost model. Make her the hero, not you.", commonMistake: "Asking Victoria to build the case herself. She's a CEO — she doesn't have time, and she's already embarrassed.", winningMove: "Say: 'Victoria, I'll build the board deck myself using your actual numbers. Three slides: ROI, competitive, and 3-year model. I'll have it to you by tomorrow. You present it — I'll stay behind the scenes.'" }
  },

  // 13. DANIEL WRIGHT — The Merger Freeze
  'daniel-wright-cio': {
    id: 'daniel-wright-cio',
    name: 'Daniel Wright',
    role: 'CIO',
    company: 'Meridian Insurance Group',
    industry: 'insurance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/daniel-wright-cio.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Merger Freeze',
    scenarioDesc: "Daniel is 53, and his company just announced a merger. All vendor contracts are frozen pending integration review. Your deal was 2 weeks from signing. Now it's in limbo with no timeline. Daniel has no authority to override the freeze. The deal isn't dead — it's frozen in corporate amber.",
    goal: 'Keep the deal alive through the freeze and position for reactivation.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "We just announced a merger. All new vendor contracts are frozen. I can't sign anything right now.", impatient1: "It's not my call. The integration team controls all new spend.", impatient2: "I don't even know if my role survives the merger.", hangup: "Call me in 6 months if I'm still here. *click*" },
    coachingNotes: { whatWorks: "Get introduced to the integration team NOW. Position your solution as helping the merger succeed — data consolidation, system standardization. Become part of the integration plan, not a frozen contract.", commonMistake: "Waiting 6 months. By then the integration team has chosen their own vendors.", winningMove: "Say: 'Daniel, I understand the freeze. Can you introduce me to the integration team? Our platform actually solves one of their biggest challenges — consolidating systems between two companies. I'd rather be part of the solution than wait in the queue.'" }
  },

  // 14. PATRICIA HALL — The Competitor Swoop
  'patricia-hall-director-of-procurement': {
    id: 'patricia-hall-director-of-procurement',
    name: 'Patricia Hall',
    role: 'Director of Procurement',
    company: 'National Healthcare Partners',
    industry: 'healthcare',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/patricia-hall-director-of-procurement.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Last-Minute Competitor',
    scenarioDesc: "Patricia is 49, and you were the chosen vendor. Then a competitor called her boss directly with a 30% lower price. Now she's been told to 'take another look' before signing. You're about to lose a deal you already won because a competitor played the executive card at the finish line.",
    goal: 'Defend your position against the last-minute competitor.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "I need to be upfront — a competitor went directly to our CEO with a 30% lower quote. I've been asked to reconsider.", impatient1: "I chose you. But my CEO is asking why we're paying 30% more.", impatient2: "Give me something to defend your price with.", hangup: "I can't justify the premium. Going with the cheaper option. *click*" },
    coachingNotes: { whatWorks: "Don't match the price. Arm Patricia with a comparison that shows total cost of ownership, not just license price. Include implementation, support, training, and risk. The competitor's 30% lower price probably excludes half the scope.", commonMistake: "Panic-matching the 30% discount. You've devalued your product and set a new baseline.", winningMove: "Say: 'Patricia, let's build a total cost comparison together. License price is one line item. When you add implementation, training, support tiers, and contract flexibility, I'll bet we're within 5%. Can we put that side-by-side for your CEO?'" }
  },

  // 15. KEVIN O'BRIEN — The Procurement Bottleneck
  'kevin-obrien-procurement-manager': {
    id: 'kevin-obrien-procurement-manager',
    name: "Kevin O'Brien",
    role: 'Procurement Manager',
    company: 'Atlantic Energy Corp',
    industry: 'energy',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/kevin-obrien-procurement-manager.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Procurement Black Hole',
    scenarioDesc: "Kevin is 46, Irish-American, and his procurement process is a black hole. Contracts go in, nothing comes out. He has 47 vendor deals in his queue and yours is number 23. He's not being difficult — he's genuinely overwhelmed. But your deal is rotting in the queue.",
    goal: 'Jump the procurement queue without alienating Kevin.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Your deal is in the queue. I have 47 contracts ahead of you. I'll get to it when I get to it.", impatient1: "Everyone thinks their deal is urgent. Get in line.", impatient2: "Pushing me won't make it faster. It'll make it slower.", hangup: "Call back next month. *click*" },
    coachingNotes: { whatWorks: "Make Kevin's life easier, not harder. Pre-fill every form, pre-answer every compliance question, and deliver a 'procurement-ready package' that he can process in 30 minutes instead of 3 days.", commonMistake: "Going over Kevin's head to his boss. He'll deprioritize your deal permanently.", winningMove: "Say: 'Kevin, I know you're slammed. I've pre-completed every procurement form, answered all compliance questions, and attached all certifications. Your processing time should be about 30 minutes, not 3 days. I just made your job easier — can we get this one off your plate quickly?'" }
  },

  // 16. ANGELA RUSSO — The Decision Fatigue
  'angela-russo-vp-of-marketing': {
    id: 'angela-russo-vp-of-marketing',
    name: 'Angela Russo',
    role: 'VP of Marketing',
    company: 'Crestline Hospitality',
    industry: 'hospitality',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/angela-russo-vp-of-marketing.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Exhausted Buyer',
    scenarioDesc: "Angela is 43, Italian-American, and has been evaluating 6 vendors for 4 months. She's exhausted. She can't tell the difference between you and the others anymore. Every demo looks the same. Every proposal blends together. She's about to make a decision based on who called last, not who's best.",
    goal: 'Cut through decision fatigue and make the choice obvious.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "I've been looking at 6 vendors for 4 months. I honestly can't tell you apart anymore.", impatient1: "Everyone says the same things. Why are you different?", impatient2: "I just want this decision to be over.", hangup: "I'm going with whoever makes this easiest. *click*" },
    coachingNotes: { whatWorks: "Make the decision FOR her. Build a one-page comparison that honestly positions you vs the top 2 alternatives. Highlight the 1-2 areas where you genuinely win, not 15 features.", commonMistake: "Doing another demo. She's seen 6 demos. She's done with demos.", winningMove: "Say: 'Angela, you don't need another demo. Here's a one-page comparison: you vs your top 2 alternatives on the 3 criteria that matter most to hospitality. We win on two, they win on one. The decision is right there.'" }
  },

  // 17. JAMES KIMURA — The Internal Champion War
  'james-kimura-vp-of-engineering': {
    id: 'james-kimura-vp-of-engineering',
    name: 'James Kimura',
    role: 'VP of Engineering',
    company: 'Stratos Cloud',
    industry: 'SaaS',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/james-kimura-vp-of-engineering.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Internal Split',
    scenarioDesc: "James is 40, Japanese-American, and he's your champion. But the VP of Sales wants a different vendor. They're in a political fight and your deal is the battlefield. James chose you on technical merit. The VP of Sales chose the competitor because his college buddy is the sales rep. The CEO will break the tie.",
    goal: 'Help James win the internal fight without getting political.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "Our VP of Sales is pushing a competitor. He and their rep go way back. I need to make a stronger case to our CEO.", impatient1: "The CEO respects data. Give me numbers, not talking points.", impatient2: "If I lose this fight, you lose the deal.", hangup: "I don't have enough ammunition. Going with the other vendor by default. *click*" },
    coachingNotes: { whatWorks: "Build James a fact-based comparison that a CEO can evaluate objectively. Include a technical proof-of-concept result, reference calls from similar companies, and total cost analysis. Facts beat friendships at CEO level.", commonMistake: "Getting dragged into internal politics or badmouthing the competitor. Stay above it.", winningMove: "Say: 'James, let's build your case on facts the CEO can't ignore: our proof-of-concept results, 3 reference calls from companies your CEO respects, and a total cost comparison. Data wins this fight.'" }
  },

  // 18. SOPHIA MARTINEZ — The Scope Shrink
  'sophia-martinez-director-of-operations': {
    id: 'sophia-martinez-director-of-operations',
    name: 'Sophia Martinez',
    role: 'Director of Operations',
    company: 'Verde Agricultural Tech',
    industry: 'agriculture',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/sophia-martinez-director-of-operations.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Shrinking Deal',
    scenarioDesc: "Sophia is 37, Mexican-American, and the deal was $120K. Then her budget got cut. Now she wants the same product for $80K. She's asking you to shrink the scope to fit the budget. But the reduced scope won't solve her actual problem — she just doesn't realize it yet.",
    goal: 'Protect the deal scope while acknowledging the budget cut.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "My budget got cut from $120K to $80K. Can we strip the scope to fit?", impatient1: "I know it won't be the full solution, but $80K is all I have.", impatient2: "Something is better than nothing, right?", hangup: "I'll just do without. Can't afford it. *click*" },
    coachingNotes: { whatWorks: "Show her that $80K scope won't solve the problem — she'll spend $80K and still have the original pain. Propose phased implementation: $80K now for Phase 1, $40K in Q3 for Phase 2. She gets the full solution on a payment plan.", commonMistake: "Stripping scope to fit the budget. She'll get an incomplete product, blame you when it doesn't work, and never buy Phase 2.", winningMove: "Say: 'Sophia, I can build a phased plan: $80K now for the core platform, then $40K in Q3 when your budget resets for the advanced modules. You get the full solution — just in two steps instead of one.'" }
  },

  // 19. ROBERT ANDERSON — The Ghost
  'robert-anderson-cto': {
    id: 'robert-anderson-cto',
    name: 'Robert Anderson',
    role: 'CTO',
    company: 'Pinehill Data Systems',
    industry: 'SaaS',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/robert-anderson-cto.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Complete Ghost',
    scenarioDesc: "Robert is 46, and he's completely vanished. 6 unanswered emails. 3 voicemails. LinkedIn message read but not replied. The deal was at final review. He hasn't said no — he just stopped responding. Something happened internally but you don't know what.",
    goal: 'Break through the ghost and get a response — even a no.',
    category: 'enterprise-closing',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'brush-off',
    silenceProfile: { pattern: 'patient', stages: [{ type: 'warn', delay: 10000, lineKey: 'impatient1' }, { type: 'hangup', delay: 20000, lineKey: 'hangup' }] },
    lines: { opening: "...[picks up unexpectedly]... Oh, hey. Yeah, sorry about that. Things got complicated.", impatient1: "It's not a no. It's just... I can't explain right now.", impatient2: "Give me another week. Maybe two.", hangup: "[silence, then line goes dead]" },
    coachingNotes: { whatWorks: "When you finally reach him, don't ask about the silence. Ask one direct question: 'Is this deal still alive?' Get a binary answer. If yes, get a specific next step with a date. If no, move on.", commonMistake: "Sending more follow-up emails. He's already ignoring 6. The 7th won't help.", winningMove: "Say: 'Robert, no need to explain. One question: is this deal still alive? If yes, what's the one thing that needs to happen next? If no, totally fine — just tell me so I can close the file.'" }
  },

  // 20. HELEN DIMITRIOU — The Consensus Collapse
  'helen-dimitriou-vp-of-product': {
    id: 'helen-dimitriou-vp-of-product',
    name: 'Helen Dimitriou',
    role: 'VP of Product',
    company: 'Olympus Learning',
    industry: 'education',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/helen-dimitriou-vp-of-product.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    scenarioTitle: 'The Committee Collapse',
    scenarioDesc: "Helen is 44, Greek-American, and the buying committee of 5 people had consensus. Then one person changed their mind at the last meeting. Now the whole group is reconsidering. One dissenter has derailed 4 months of alignment. Helen is your champion but she's losing control of the room.",
    goal: 'Neutralize the dissenter and restore consensus.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "We had consensus until last Tuesday. Our Head of Engineering changed his mind. Now everyone's wavering.", impatient1: "His concern is about integration complexity. The others are following his doubt.", impatient2: "If I can't get him back on board, this deal is dead.", hangup: "I can't fight my own team. We're going to table this. *click*" },
    coachingNotes: { whatWorks: "Offer to meet the dissenter directly. Address his specific concern — integration complexity — with a technical deep-dive. Don't try to go around him. Win him back.", commonMistake: "Ignoring the dissenter and pushing the other 4 to overrule him. He'll undermine implementation.", winningMove: "Say: 'Helen, can I get 30 minutes with your Head of Engineering? I'll bring our lead architect. We'll address the integration concern directly and build a technical implementation plan together. If we win him back, the others follow.'" }
  },

  // ═══════════════════════════════════════════════════════════════
  // ADVANCED (15 characters)
  // ═══════════════════════════════════════════════════════════════

  // 21. CATHERINE BLACKWELL — The Executive Bypass
  'catherine-blackwell-chief-revenue-officer': {
    id: 'catherine-blackwell-chief-revenue-officer',
    name: 'Catherine Blackwell',
    role: 'Chief Revenue Officer',
    company: 'TerraVault Technologies',
    industry: 'SaaS',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/catherine-blackwell-chief-revenue-officer.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Executive Bypass',
    scenarioDesc: "Catherine is 48, British-American, and just sent your deal back to procurement after you went over her team's head to reach her directly. She's offended. Her team felt disrespected. You may have killed the deal trying to accelerate it. Now you need to repair the damage AND close.",
    goal: 'Repair the relationship damage and get back on track.',
    category: 'enterprise-closing',
    personalityType: 'hostile',
    objectionStyle: 'brush-off',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 10000, lineKey: 'hangup' }] },
    lines: { opening: "You went over my team's head. That's not how we do business here.", impatient1: "My procurement team felt bypassed. They're not happy.", impatient2: "Give me one reason not to send this to your competitor right now.", hangup: "We're done. Going with someone who respects our process. *click*" },
    coachingNotes: { whatWorks: "Apologize directly — no spin, no excuses. Then ask how to make it right with her team. Offer to restart the process through proper channels.", commonMistake: "Defending the bypass with 'I was just trying to move things forward.' She knows why you did it. She's angry about HOW.", winningMove: "Say: 'Catherine, I owe you and your team an apology. I should have worked through procurement, not around them. I'd like to call your procurement lead personally to apologize and restart through the proper process. Can you tell me who to call?'" }
  },

  // 22. MOHAMMAD HASSAN — The Geopolitical Risk
  'mohammad-hassan-coo': {
    id: 'mohammad-hassan-coo',
    name: 'Mohammad Hassan',
    role: 'COO',
    company: 'Crescent Energy Partners',
    industry: 'energy',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/mohammad-hassan-coo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Regulatory Freeze',
    scenarioDesc: "Mohammad is 51, Jordanian-American, and new government regulations just made your product's data handling non-compliant in 3 of his 8 operating countries. The deal was final. Now legal is reviewing whether they can even use you. The regulations weren't your fault but they're your problem.",
    goal: 'Demonstrate compliance or propose a compliant workaround.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "New data regulations just made your platform non-compliant in 3 of our markets. We can't sign until this is resolved.", impatient1: "Our legal team says the data residency requirements conflict with your architecture.", impatient2: "Can you host data locally in each country?", hangup: "Non-compliant means non-starter. Goodbye. *click*" },
    coachingNotes: { whatWorks: "Offer regional data hosting or a hybrid architecture that keeps regulated data in-country. Show a compliance roadmap with specific dates.", commonMistake: "Saying 'we'll fix it eventually.' He needs a timeline, not a promise.", winningMove: "Say: 'Mohammad, we can deploy regional data nodes in all 3 countries within 60 days. Regulated data stays in-country, everything else centralizes. I'll have our compliance team send you the architecture diagram and a written compliance attestation by Friday.'" }
  },

  // 23. DIANE WEBER — The CFO Holdout
  'diane-weber-cfo': {
    id: 'diane-weber-cfo',
    name: 'Diane Weber',
    role: 'CFO',
    company: 'Industrial Dynamics Corp',
    industry: 'manufacturing',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/diane-weber-cfo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The CFO Who Wasn\'t Sold',
    scenarioDesc: "Diane is 55, German-American, and everyone on the buying team is sold except her. She controls the budget. She's not against you — she just hasn't seen a financial case that justifies the investment. The team wants emotion. She wants math. Without her signature, nothing moves.",
    goal: 'Build a financial case that satisfies a CFO\'s rigor.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "The team loves you. I don't see the ROI. Show me the financial case.", impatient1: "I need payback period, NPV, and risk-adjusted returns. Not features.", impatient2: "If the math doesn't work, I don't sign. Period.", hangup: "No financial case, no deal. *click*" },
    coachingNotes: { whatWorks: "Build a CFO-grade financial model: payback period, NPV, IRR, and sensitivity analysis. Use HER company's actual numbers, not generic benchmarks.", commonMistake: "Showing her a marketing ROI calculator. She'll see through it in 10 seconds.", winningMove: "Say: 'Diane, I built a financial model using your actual operational costs. Payback period is 11 months, NPV over 3 years is $340K, and even in the worst-case scenario you break even in 18 months. I'll send you the spreadsheet so you can stress-test the assumptions yourself.'" }
  },

  // 24. BRIAN CALLAHAN — The Contract Ransom
  'brian-callahan-account-director-incumbent-vendor': {
    id: 'brian-callahan-account-director-incumbent-vendor',
    name: 'Brian Callahan',
    role: 'Account Director (incumbent vendor)',
    company: "Client's current vendor",
    industry: 'SaaS',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/brian-callahan-account-director-incumbent-vendor.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Vendor Lock-In Counter',
    scenarioDesc: "Brian isn't your buyer — he's the incumbent vendor's account director. He just offered your prospect a 40% renewal discount to stay. He's also threatening 6-month data migration delays if they leave. Your deal is done but the incumbent is fighting dirty to keep the account.",
    goal: 'Help your prospect see through the incumbent\'s retention tactics.',
    category: 'enterprise-closing',
    personalityType: 'hostile',
    objectionStyle: 'vendor-loyal',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 10000, lineKey: 'hangup' }] },
    lines: { opening: "[Your prospect says:] Their current vendor just offered 40% off to stay. And they're saying migration will take 6 months.", impatient1: "If migration really takes 6 months, maybe we should just renew.", impatient2: "A 40% discount is hard to walk away from.", hangup: "I'm going to take their renewal offer. It's safer. *click*" },
    coachingNotes: { whatWorks: "Ask why they're offering 40% NOW when they never offered it before. If the product was worth full price, why the panic discount? And migration doesn't take 6 months — they're inflating the timeline to scare the prospect.", commonMistake: "Matching the 40% discount. You're competing with a retention offer that will evaporate after renewal.", winningMove: "Say: 'Ask yourself — if they could offer 40% off, why were they charging you full price for 3 years? That discount disappears next renewal. And migration? We've done it in 30 days for companies larger than yours. I'll put a migration timeline guarantee in the contract.'" }
  },

  // 25. ELENA VOLKOV — The Deadline Manipulation
  'elena-volkov-coo': {
    id: 'elena-volkov-coo',
    name: 'Elena Volkov',
    role: 'COO',
    company: 'BioStar Therapeutics',
    industry: 'pharma',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/elena-volkov-coo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Manufactured Urgency',
    scenarioDesc: "Elena is 47, Russian-American, and she's created a fake deadline. 'We need to decide by Friday or we go with someone else.' But her procurement process takes 3 weeks minimum. She's using manufactured urgency to extract a panic discount. The deadline isn't real — but your fear of losing the deal is.",
    goal: 'Call the bluff on the manufactured deadline.',
    category: 'enterprise-closing',
    personalityType: 'hostile',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 5000, lineKey: 'impatient1' }, { type: 'hangup', delay: 10000, lineKey: 'hangup' }] },
    lines: { opening: "We're deciding by Friday. If you can't sharpen the pencil by then, we go with someone else.", impatient1: "Friday is final. 5pm. Best and final offer.", impatient2: "Three vendors are all getting the same deadline.", hangup: "Too slow. Going with your competitor. *click*" },
    coachingNotes: { whatWorks: "Calmly say you're happy to submit your best offer, but it's already your best offer. If her procurement process genuinely moves that fast, great. If not, you'll still be here Monday.", commonMistake: "Panic-discounting to meet a fake deadline. She'll pocket the discount and the real decision will happen in 3 weeks anyway.", winningMove: "Say: 'Elena, I appreciate the timeline. Our proposal IS our best offer — I'm not going to inflate it so I can pretend to discount it on Friday. If we're the right choice at this price, great. If not, that's OK too. Quality decisions don't need artificial urgency.'" }
  },

  // 26. ANTHONY GRANT — The Layoff Shadow
  'anthony-grant-vp-of-operations': {
    id: 'anthony-grant-vp-of-operations',
    name: 'Anthony Grant',
    role: 'VP of Operations',
    company: 'Pacific Consolidated',
    industry: 'logistics',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/anthony-grant-vp-of-operations.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Layoff Shadow',
    scenarioDesc: "Anthony is 50, African-American, and rumors of layoffs are swirling at his company. He's afraid to sign a big contract because if cuts come, he'll be the person who committed $200K right before headcount reduction. He's protecting himself, not rejecting your product.",
    goal: 'Structure the deal to reduce his personal risk.',
    category: 'enterprise-closing',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "I'm not going to lie — timing is bad. There are rumors about restructuring. I don't want to commit $200K right now.", impatient1: "If I sign this and then there are cuts, I'm the guy who spent $200K at the wrong time.", impatient2: "Can we do something smaller? Less risk?", hangup: "I'm going to wait until things settle. *click*" },
    coachingNotes: { whatWorks: "Offer a quarterly commitment instead of annual. Or a 90-day cancellation clause. Reduce his personal exposure while keeping the deal alive.", commonMistake: "Ignoring his fear and pushing the full deal. He's trying to survive, not negotiate.", winningMove: "Say: 'Anthony, let's do this quarterly. $50K per quarter with a 60-day opt-out. If restructuring happens, you can pause or cancel. No long-term exposure. You get the product you need without the career risk.'" }
  },

  // 27. NINA PETROV — The Multi-Year Commitment Fear
  'nina-petrov-cfo': {
    id: 'nina-petrov-cfo',
    name: 'Nina Petrov',
    role: 'CFO',
    company: 'Stellar Manufacturing',
    industry: 'manufacturing',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/nina-petrov-cfo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Commitment Phobia',
    scenarioDesc: "Nina is 43, Bulgarian-American, and loves your product but refuses to sign anything longer than month-to-month. She was burned by a 3-year contract with a vendor who raised prices, degraded support, and held her hostage. She has commitment PTSD. Your annual contract terrifies her.",
    goal: 'Get an annual commitment from someone with vendor trust issues.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Month-to-month only. I'm never signing a long-term contract again.", impatient1: "My last vendor locked me in for 3 years and the product got worse every quarter.", impatient2: "If you're confident in your product, month-to-month shouldn't scare you.", hangup: "Month-to-month or I walk. *click*" },
    coachingNotes: { whatWorks: "Offer annual with a quarterly exit ramp. She pays annual pricing but can leave every 90 days with notice. She gets the safety, you get the commitment structure.", commonMistake: "Accepting month-to-month at annual pricing. You'll churn her in 4 months when budget gets tight.", winningMove: "Say: 'Nina, annual contract, quarterly exit ramp. Every 90 days, you can leave with 30 days notice and a prorated refund. If we're not delivering, you walk. You get annual pricing without the lock-in fear.'" }
  },

  // 28. CHARLES WELLINGTON — The Political Appointee
  'charles-wellington-board-chairman': {
    id: 'charles-wellington-board-chairman',
    name: 'Charles Wellington',
    role: 'Board Chairman',
    company: 'Dominion Healthcare System',
    industry: 'healthcare',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/charles-wellington-board-chairman.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Political Veto',
    scenarioDesc: "Charles is 64, old-money, and the board chair just vetoed your deal because a board member sits on the advisory board of your competitor. It's pure politics — nothing to do with your product. The entire buying team is frustrated. You need to find a way around a political veto.",
    goal: 'Navigate a politically-motivated veto.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "The board has decided to go in a different direction. I appreciate your team's effort.", impatient1: "This isn't about your product. The decision has been made.", impatient2: "I'd suggest you maintain the relationship for future opportunities.", hangup: "The matter is closed. *click*" },
    coachingNotes: { whatWorks: "Don't fight the veto directly. Ask the buying team to request a formal evaluation scorecard from the board. Political vetoes don't survive documented evidence that the chosen vendor scored lower.", commonMistake: "Accepting the veto gracefully. Sometimes you need to fight — politely but firmly.", winningMove: "Say: 'Charles, I respect the board's authority. Would it be reasonable to request that both vendors go through a formal scored evaluation? If the other vendor wins on merit, I'll step aside. We just want a fair comparison.'" }
  },

  // 29. JESSICA THORNTON — The Budget Redirect
  'jessica-thornton-vp-of-clinical-operations': {
    id: 'jessica-thornton-vp-of-clinical-operations',
    name: 'Jessica Thornton',
    role: 'VP of Clinical Operations',
    company: 'Premier Health Network',
    industry: 'healthcare',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/jessica-thornton-vp-of-clinical-operations.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Budget Steal',
    scenarioDesc: "Jessica is 46, and her approved budget for your deal just got redirected to another department's emergency project. She still has authority to spend — but no money. The CFO took her budget. She needs to find money from somewhere else or the deal dies.",
    goal: 'Help Jessica find alternative budget sources.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'price-first',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 13000, lineKey: 'hangup' }] },
    lines: { opening: "I still want this. But the CFO redirected my budget to IT infrastructure. I have zero dollars.", impatient1: "Can we do this for free for 6 months and then I'll fund it next fiscal year?", impatient2: "Is there any creative financing you can offer?", hangup: "No budget, no deal. I'm sorry. *click*" },
    coachingNotes: { whatWorks: "Explore alternative budget sources: shared services budget, innovation fund, cost-savings reinvestment, or cross-department cost sharing. She wants to buy — help her find the money.", commonMistake: "Offering 6 months free. You'll never get paid and you've set a precedent.", winningMove: "Say: 'Jessica, three ideas: 1) Does your org have an innovation fund or transformation budget? 2) Can we split the cost with another department that also benefits? 3) I can offer net-90 payment — sign now, first invoice in 3 months when next quarter's budget opens.'" }
  },

  // 30. RICHARD BLAKE — The Revenge Deal
  'richard-blake-former-vp-fired': {
    id: 'richard-blake-former-vp-fired',
    name: 'Richard Blake',
    role: 'Former VP (fired)',
    company: 'Evergreen Capital',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/richard-blake-former-vp-fired.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Toxic Champion',
    scenarioDesc: "Richard was your biggest champion. He was fired last week for performance issues. Now the new leadership views EVERYTHING he touched with suspicion — including your deal. You're guilty by association. The deal isn't dead because of your product — it's dead because Richard picked you.",
    goal: 'Dissociate your deal from the fired champion.',
    category: 'enterprise-closing',
    personalityType: 'skeptical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "[New VP speaking:] Richard picked you. He's gone. We're reviewing all of his decisions.", impatient1: "Everything Richard touched is under review. Nothing personal.", impatient2: "Can you justify this deal independent of Richard's recommendation?", hangup: "We're going in a different direction. *click*" },
    coachingNotes: { whatWorks: "Distance yourself from Richard immediately. Present the deal on its OWN merits to the new leadership. Never mention Richard or reference his endorsement.", commonMistake: "Defending Richard or saying 'but he approved this for good reasons.' His name is toxic now.", winningMove: "Say: 'I understand completely. Let's start fresh. Forget who recommended us — here's the business case on its own merits. I'd like to present it to your team as if we're meeting for the first time. Fair?'" }
  },

  // 31. YUKI TANAKA — The Cultural Misread
  'yuki-tanaka-director-of-international-operations': {
    id: 'yuki-tanaka-director-of-international-operations',
    name: 'Yuki Tanaka',
    role: 'Director of International Operations',
    company: 'Sakura Global Trading',
    industry: 'trading',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/yuki-tanaka-director-of-international-operations.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Cultural Gap',
    scenarioDesc: "Yuki is 48, Japanese, and has been saying 'yes' politely for 6 weeks. In Japanese business culture, 'yes' often means 'I hear you,' not 'I agree.' You've been celebrating a deal that was never actually agreed. The real decision requires consensus from Tokyo HQ, which hasn't been consulted.",
    goal: 'Identify the real decision process and align with Tokyo HQ.',
    category: 'enterprise-closing',
    personalityType: 'polite-but-firm',
    objectionStyle: 'not-interested',
    silenceProfile: { pattern: 'double', stages: [{ type: 'warn', delay: 8000, lineKey: 'impatient1' }, { type: 'warn', delay: 16000, lineKey: 'impatient2' }, { type: 'hangup', delay: 22000, lineKey: 'hangup' }] },
    lines: { opening: "We are very interested in continuing the discussion. We appreciate your patience.", impatient1: "We would need to review this with our Tokyo headquarters. It is our process.", impatient2: "Perhaps we can arrange a presentation for the senior team in Tokyo.", hangup: "Thank you for your understanding. We will be in touch. *click*" },
    coachingNotes: { whatWorks: "Recognize that consensus (nemawashi) is the real decision process. Ask directly: 'Who else needs to be involved in this decision?' Offer to present to Tokyo HQ directly.", commonMistake: "Assuming 'yes' means the deal is done. In many Japanese business contexts, it means acknowledgment, not agreement.", winningMove: "Say: 'Yuki-san, I want to make sure we align with your entire team. Would it be helpful for me to prepare a presentation for your Tokyo headquarters? I want everyone comfortable with this decision.'" }
  },

  // 32. FRANK MORRISON — The Sunset Vendor
  'frank-morrison-cio': {
    id: 'frank-morrison-cio',
    name: 'Frank Morrison',
    role: 'CIO',
    company: 'Heritage Banking Corp',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/frank-morrison-cio.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Legacy Trap',
    scenarioDesc: "Frank is 58, and his company runs on a 15-year-old system that's being sunset. He MUST move to a new vendor. But he's terrified of change. Every conversation circles back to 'but it works now.' He's choosing the pain he knows over the pain he doesn't. Fear of change is stronger than his need for change.",
    goal: 'Help Frank see that staying is riskier than switching.',
    category: 'enterprise-closing',
    personalityType: 'skeptical',
    objectionStyle: 'vendor-loyal',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "I know we need to move. But the old system works. Why fix what isn't broken?", impatient1: "We've been on this platform for 15 years. My team knows it inside out.", impatient2: "What if the migration fails? We could lose everything.", hangup: "I'm going to extend with the current vendor one more year. *click*" },
    coachingNotes: { whatWorks: "Quantify the cost of staying: security risks, compliance gaps, inability to hire engineers for legacy tech, and the vendor's sunset date. Make the risk of NOT switching scarier than switching.", commonMistake: "Bashing the old system. He built his career on it. Show respect while showing the risk.", winningMove: "Say: 'Frank, your current system served you well for 15 years. But the vendor ends support in 14 months. After that: no security patches, no compliance updates, no help desk. The risk of staying is now greater than the risk of moving. Let me show you our migration plan — 90 days, zero downtime.'" }
  },

  // 33. ANDREA SILVA — The Reference Sabotage
  'andrea-silva-vp-of-hr': {
    id: 'andrea-silva-vp-of-hr',
    name: 'Andrea Silva',
    role: 'VP of HR',
    company: 'Pinnacle Staffing Group',
    industry: 'HR services',
    tier: 'midmarket',
    avatar: '/sales-negotiation/characters/enterprise-close/andrea-silva-vp-of-hr.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Bad Reference',
    scenarioDesc: "Andrea is 41, Brazilian-American, and just spoke with one of your references — who gave a negative review. The reference had a bad implementation experience 2 years ago (which has since been fixed). But the damage is done. Andrea is now questioning everything.",
    goal: 'Recover from a bad reference without dismissing it.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 6000, lineKey: 'impatient1' }, { type: 'hangup', delay: 12000, lineKey: 'hangup' }] },
    lines: { opening: "I spoke with your reference at DataCorp. They said implementation was a disaster. What happened?", impatient1: "They said it took 6 months instead of 6 weeks.", impatient2: "How do I know the same thing won't happen to us?", hangup: "I can't take that risk. Going elsewhere. *click*" },
    coachingNotes: { whatWorks: "Own it. Don't dismiss the reference. Explain what went wrong, what you changed since then, and offer a more recent reference from a similar implementation.", commonMistake: "Saying 'they had unique circumstances.' She'll think you make excuses instead of fixing problems.", winningMove: "Say: 'Andrea, that's a fair concern. DataCorp's implementation was 2 years ago and we made serious mistakes. Here's what we changed: dedicated implementation managers, 30-day milestone checkpoints, and a penalty clause if we miss timelines. Can I connect you with our most recent implementation — completed in 5 weeks?'" }
  },

  // 34. MARCUS SHAW — The Deal Committee
  'marcus-shaw-chief-commercial-officer': {
    id: 'marcus-shaw-chief-commercial-officer',
    name: 'Marcus Shaw',
    role: 'Chief Commercial Officer',
    company: 'Continental Logistics',
    industry: 'logistics',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/marcus-shaw-chief-commercial-officer.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Deal Desk Rejection',
    scenarioDesc: "Marcus is 52, African-American, and his internal deal desk rejected the pricing structure your sales team proposed. Not because it's too expensive — because it doesn't fit their approval framework. Annual pricing needs to be broken into quarterly line items with specific cost centers. It's bureaucratic, not adversarial.",
    goal: 'Restructure the deal to fit internal approval requirements.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'single', stages: [{ type: 'warn', delay: 7000, lineKey: 'impatient1' }, { type: 'hangup', delay: 14000, lineKey: 'hangup' }] },
    lines: { opening: "Deal desk rejected the proposal format. They need quarterly invoicing with cost center allocation. Can you restructure?", impatient1: "Same total price — just broken into 4 quarterly invoices with specific GL codes.", impatient2: "Our ERP can't process annual lump-sum payments from new vendors.", hangup: "If you can't invoice quarterly, we can't process the PO. *click*" },
    coachingNotes: { whatWorks: "Restructure the contract as 4 quarterly invoices at 25% each, with GL codes and cost centers pre-filled. The total price doesn't change — just the format.", commonMistake: "Insisting on annual prepayment because 'that's our standard.' Their ERP doesn't care about your standard.", winningMove: "Say: 'Marcus, I'll restructure as 4 quarterly invoices with your GL codes pre-populated. Same total, same terms. I just need your cost center numbers and I'll have the revised contract to you by end of day.'" }
  },

  // 35. DIANA OSEI-MENSAH — The Ethical Dilemma
  'diana-osei-mensah-chief-ethics-officer': {
    id: 'diana-osei-mensah-chief-ethics-officer',
    name: 'Diana Osei-Mensah',
    role: 'Chief Ethics Officer',
    company: 'Sovereign Wealth Advisors',
    industry: 'finance',
    tier: 'enterprise',
    avatar: '/sales-negotiation/characters/enterprise-close/diana-osei-mensah-chief-ethics-officer.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    scenarioTitle: 'The Ethics Review',
    scenarioDesc: "Diana is 49, Ghanaian-British, and your deal is held up because your company sells to an industry her ethics board has flagged. You sell to military contractors. Her firm manages ESG-focused sovereign wealth funds. The ethics review could take months — or kill the deal entirely.",
    goal: 'Navigate the ethics review and find a path to approval.',
    category: 'enterprise-closing',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: { pattern: 'double', stages: [{ type: 'warn', delay: 8000, lineKey: 'impatient1' }, { type: 'warn', delay: 16000, lineKey: 'impatient2' }, { type: 'hangup', delay: 22000, lineKey: 'hangup' }] },
    lines: { opening: "Our ethics board has flagged your client list. You serve defense contractors. We manage ESG-mandated funds.", impatient1: "Our stakeholders require full supply chain ethical transparency.", impatient2: "Can you provide a complete client industry breakdown and your ESG compliance documentation?", hangup: "Ethics review denied. We cannot proceed. *click*" },
    coachingNotes: { whatWorks: "Provide full transparency: ESG report, diversity metrics, environmental certifications, and offer a contractual clause that your work for them will be firewalled from defense clients.", commonMistake: "Hiding your client list or getting defensive about defense clients. She'll find out anyway.", winningMove: "Say: 'Diana, full transparency: here's our complete ESG report, our client industry breakdown, and our sustainability certifications. I'll also propose a contractual firewall: your data, your team, and your work product are completely separated from any defense-related operations. Would that address the ethics board's concerns?'" }
  },

};